import ReactDOM from 'react-dom/client';
import './index.css';
import { registerStorage, registerOnlineStatus } from '@watchlist/shared';
import { WebStorage, WebOnlineStatus } from './platform';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { offlineStorage } from './services/offlineStorage';
import { syncService } from './services/syncService';
import { Capacitor } from '@capacitor/core';
import { initializeCapacitor, isNative } from './capacitor-init';
import { SearchService } from './services/searchService';
import { warmUpMovieFunctions } from './services/tmdbFunctionsApi';
import { registerEarlyIosNotificationTapRouting } from './services/pushNotificationService';
import { bootCoordinator } from './services/bootCoordinator';
import { initializeSafeAreaHandling } from './utils/platformUtils';
import './utils/migrateMovieIds';
import { getFontFamily, getFontUrl } from './config/fonts';
import './utils/fontPreview'; // Load font preview utilities in development
import { initializeWebVitals, initializePerformanceMonitoring } from './utils/performanceMonitoring';

// Register platform implementations FIRST (before any shared service usage)
registerStorage(new WebStorage());
registerOnlineStatus(new WebOnlineStatus());

// Load and set font configuration
const fontUrl = getFontUrl();
if (fontUrl) {
  // Load Google Font dynamically
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
}

// Set font family CSS variable
document.documentElement.style.setProperty('--app-font-family', getFontFamily());

/**
 * Start the application
 * On native platforms, coordinates boot sequence before rendering
 */
async function startApp() {
  console.log('Starting Watchlists app...');
  console.log('Platform:', isNative() ? 'native (Capacitor)' : 'web');

  // Initialize safe area CSS custom properties based on platform
  // Must be called early to avoid layout shifts
  initializeSafeAreaHandling();

  // Initialize Capacitor plugins (status bar, keyboard, deep links)
  // Note: Splash screen is now handled by bootCoordinator
  try {
    await initializeCapacitor();
    console.log('✅ Capacitor plugins initialized');
    // iOS: register FCM tap routing before React/auth — otherwise cold-start taps fire before listeners exist.
    if (isNative() && Capacitor.getPlatform() === 'ios') {
      registerEarlyIosNotificationTapRouting();
    }
  } catch (error) {
    console.error('❌ Failed to initialize Capacitor:', error);
  }

  // On native platforms, coordinate boot sequence BEFORE rendering
  // This ensures Firebase is ready and auth is resolved
  if (isNative()) {
    console.log('Native platform detected, starting coordinated boot...');

    // Don't await - let boot run in parallel with initial render
    // App.tsx will show BootScreen until boot is complete
    // bootCoordinator handles Firebase init internally
    bootCoordinator.boot().catch((error) => {
      console.error('Boot coordinator error:', error);
    });
  }

  // Render the app
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(<App />);

  // Core Web Vitals → Google Analytics (web + Capacitor WebView)
  initializeWebVitals();

  // Initialize web-only features
  if (!isNative()) {
    // Initialize Firebase Performance Monitoring (custom traces; not enabled in native WebView)
    initializePerformanceMonitoring().then((perf) => {
      if (perf) {
        console.log('✅ Firebase Performance Monitoring initialized');
      } else {
        console.warn(
          '⚠️ Firebase Performance SDK unavailable (enable Performance in Firebase Console → Build → Performance Monitoring, or expect delay after first deploy).'
        );
      }
    }).catch((error) => {
      console.warn('⚠️ Firebase Performance Monitoring failed:', error);
    });

    // Register service worker for offline support (web PWA only)
    serviceWorkerRegistration.register({
      onSuccess: () => {
        console.log('✅ Service worker registered successfully');
      },
      onUpdate: () => {
        console.log('🔄 New service worker available');
      },
    });
  }

  // Initialize offline storage (both platforms)
  try {
    await offlineStorage.initialize();
    console.log('✅ Offline storage initialized');

    // Start background sync service
    syncService.startAutoSync();
    console.log('✅ Background sync started');
  } catch (error) {
    console.error('❌ Failed to initialize offline storage:', error);
  }
}

// Start the app
startApp(); 